import { Core, Decorative } from "@faire/design-tokens";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Position } from "@faire/web--source/slate/Tooltip/common";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackInsiderBrandPageHeaderEnrollClick } from "@faire/web-api--source/events/insider/click/brandPageHeaderEnroll";
import { trackInsiderBrandPageHeaderResumeClick } from "@faire/web-api--source/events/insider/click/brandPageHeaderResume";
import { trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick } from "@faire/web-api--source/events/retailerTeamManagement/click/insiderMarketingPageLearnMoreRedirect";
import { IInsiderLabel } from "@faire/web-api--source/indigofair/data/IInsiderLabel";
import { INSIDER_BADGE_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/INSIDER_BADGE_TOOLTIP_SEEN";
import { INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN";
import { IInsiderModalRequest } from "@faire/web-api--source/indigofair/insider/IInsiderModalRequest";
import insiderMarketingPage from "@faire/web-api--source/routes/user/insider/info";
import React from "react";
import styled from "styled-components";

import { useInsiderState } from "@faire/retailer/hooks/insider/useInsiderState";
import { getSettingInsiderThresholdForNonInsidersEnabled } from "@faire/retailer/settings/getSettingInsiderThresholdForNonInsidersEnabled";
import { useMilestone } from "@faire/retailer/stores/domain/useMilestone";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

interface IProps {
  insiderLabel?: IInsiderLabel;
  tooltipPosition?: Position;
  className?: string;
  context?: IInsiderModalRequest.InsiderModalContext;
}

/**
 * BE Driven Insider Benefits label component
 */
export const InsiderLabel: React.FC<IProps> = ({
  className,
  insiderLabel,
  tooltipPosition,
  context = IInsiderModalRequest.InsiderModalContext.BRAND_PAGE,
}) => {
  const { isTabletAndAbove } = useViewport();

  if (!insiderLabel) {
    return null;
  }

  const { text, cta_text, tooltip_message } = insiderLabel;

  const showTooltip = tooltip_message && isTabletAndAbove;

  return (
    <Row className={className} gap={4} data-test-id="insider-label">
      <StyledInsider
        small
        color={Decorative.icon.teal}
        fill={Decorative.icon.teal}
        fontSize="16px"
      />

      <Typography variant="paragraphSansMedium" color={Decorative.icon.teal}>
        {text}
        {cta_text ? <CtaText text={cta_text} context={context} /> : null}
      </Typography>
      {showTooltip ? (
        <LabelTooltip
          tooltipPosition={tooltipPosition}
          insiderLabel={insiderLabel}
        />
      ) : null}
    </Row>
  );
};

const LabelTooltip: React.FC<Partial<IProps>> = ({
  tooltipPosition,
  insiderLabel,
}) => {
  const { isInsiderActive } = useInsiderState();
  const {
    hasOccurred: hasSeenThresholdBadgeTooltip,
    setHasOccurred: setHasSeenThresholdBadgeTooltip,
  } = useMilestone(INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN);
  const {
    hasOccurred: hasSeenInsiderBadgeTooltip,
    setHasOccurred: setHasSeenInsiderBadgeTooltip,
  } = useMilestone(INSIDER_BADGE_TOOLTIP_SEEN);

  const tooltipRef = useDoActionWhenVisible(() => {
    if (isInsiderActive && getSettingInsiderThresholdForNonInsidersEnabled()) {
      if (!hasSeenThresholdBadgeTooltip && insiderLabel?.tooltip_title) {
        setHasSeenThresholdBadgeTooltip();
      } else if (!hasSeenInsiderBadgeTooltip) {
        setHasSeenInsiderBadgeTooltip();
      }
    }
  });

  if (!insiderLabel) {
    return null;
  }

  const { tooltip_message, tooltip_cta_text, tooltip_cta_url, tooltip_title } =
    insiderLabel;
  return (
    <StyledTooltip
      iconColor={Core.icon.subdued}
      position={tooltipPosition}
      innerRef={tooltipRef}
    >
      {tooltip_title ? (
        <Typography color={Core.text.inverse} variant="paragraphSansMedium">
          {tooltip_title}
        </Typography>
      ) : null}
      <Typography color={Core.text.inverse} variant="paragraphSansRegular">
        {tooltip_message}
      </Typography>
      {tooltip_cta_text && tooltip_cta_url ? (
        <>
          <Spacer height="2x" />
          <Button
            variant="tertiary"
            tertiaryColor={Core.text.inverse}
            linkTo={tooltip_cta_url}
            target="_blank"
          >
            {tooltip_cta_text}
          </Button>
        </>
      ) : null}
    </StyledTooltip>
  );
};

const StyledInsider = styled(Insider)`
  margin-top: 3px;
  align-self: flex-start;
`;

interface ICtaTextProps {
  text: string;
  context: IInsiderModalRequest.InsiderModalContext;
}

const CtaText: React.FC<ICtaTextProps> = ({ text, context }) => {
  const { isInsiderPaused } = useInsiderState();

  const { userCanPlaceOrder } = useUserStore(["userCanPlaceOrder"]);

  const linkTo = !userCanPlaceOrder
    ? insiderMarketingPage()
    : getCurrentPath().append({ insiderEnrolment: context }).toString();

  const onClick = () => {
    if (!userCanPlaceOrder) {
      trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick(
        "insider-label"
      );
    }

    if (isInsiderPaused) {
      trackInsiderBrandPageHeaderResumeClick();
    } else {
      trackInsiderBrandPageHeaderEnrollClick();
    }
  };

  return (
    <>
      {" "}
      <Button
        variant="tertiary"
        linkTo={linkTo}
        onClick={onClick}
        data-test-id="insider-label-cta"
      >
        {text}
      </Button>
    </>
  );
};

const StyledTooltip = styled(Tooltip)`
  vertical-align: text-top;
`;
